// eslint-disable-next-line no-restricted-imports
import Fuse, { FuseResult, FuseResultMatch } from 'fuse.js';
import React from 'react';

import { Beneficiary } from '@/types/beneficiary';

export interface FilteredBeneficiary {
  readonly item: Beneficiary;
  readonly matchIndices?: ReadonlyArray<readonly [number, number]>;
}

interface UseBeneficiarySearchResult {
  readonly filteredBeneficiaries: readonly FilteredBeneficiary[];
  readonly onSearchKeywordChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  readonly clearSearchKeyword: () => void;
}

export const useBeneficiarySearch = (
  beneficiaries: readonly Beneficiary[],
): UseBeneficiarySearchResult => {
  const [keyword, setKeyword] = React.useState('');

  const fuse = React.useMemo(
    (): Fuse<Beneficiary> =>
      new Fuse(beneficiaries, {
        keys: ['name'],
        threshold: 0.3,
        includeMatches: true,
      }),
    [beneficiaries],
  );

  const filteredBeneficiaries = React.useMemo((): readonly FilteredBeneficiary[] => {
    if (keyword.trim() !== '') {
      const results = fuse.search(keyword);
      return results.map((result: FuseResult<Beneficiary>): FilteredBeneficiary => {
        const match = result.matches?.find((m: FuseResultMatch): boolean => m.key === 'name');
        return {
          item: result.item,
          matchIndices: match?.indices as ReadonlyArray<readonly [number, number]> | undefined,
        };
      });
    }
    return beneficiaries.map((item: Beneficiary): FilteredBeneficiary => ({ item }));
  }, [fuse, keyword, beneficiaries]);

  const onSearchKeywordChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setKeyword(event.target.value);
    },
    [],
  );

  const clearSearchKeyword = React.useCallback((): void => {
    setKeyword('');
  }, []);

  return {
    filteredBeneficiaries: filteredBeneficiaries,
    onSearchKeywordChange: onSearchKeywordChange,
    clearSearchKeyword: clearSearchKeyword,
  };
};
